import { toast } from "react-toastify";
import type { UseMutateFunction } from "@tanstack/react-query";
import {
    buildFacetUpdatePayload,
    resolvedEtlJsonForPersist,
    type FacetSnapshotForUpdate,
} from "./facet-workspace-update-helpers";
import {
    facetResultFromUpdateResponse,
    facetServerVersionFromUpdateResponse,
} from "./facet-etl-execute-helpers";
import type { UpdateFacetPayload } from "../../tars-hooks/useUpdateFacet";
import { safe_get } from "../../../../report-visuals";

export type WorkspaceEtlFallback = "persist" | "etl-field";

export function currentFacetResult(facet: FacetSnapshotForUpdate): Record<string, unknown> {
    return (safe_get(facet, "facet_result", {}) as Record<string, unknown>) ?? {};
}

/** Parse `jsonDraft` when valid; otherwise fall back without changing existing behavior. */
export function resolveWorkspaceEtlJson(
    facet: FacetSnapshotForUpdate,
    jsonDraft?: string,
    fallback: WorkspaceEtlFallback = "persist"
): Record<string, unknown> {
    const raw = jsonDraft?.trim();
    if (raw) {
        try {
            const parsed = JSON.parse(raw) as unknown;
            if (parsed != null && typeof parsed === "object" && !Array.isArray(parsed)) {
                return parsed as Record<string, unknown>;
            }
        } catch {
            /* fall through */
        }
    }
    if (fallback === "etl-field") {
        return (facet.etl_json as Record<string, unknown> | undefined) ?? {};
    }
    return resolvedEtlJsonForPersist(facet);
}

export function syncFacetResultToStore(
    updateFacet: (id: string, updates: Record<string, unknown>) => void,
    facetId: string,
    resp: unknown,
    fallbackResult: Record<string, unknown>
): Record<string, unknown> {
    const savedFacetResult = facetResultFromUpdateResponse(resp) ?? fallbackResult;
    const ver = facetServerVersionFromUpdateResponse(resp);
    updateFacet(facetId, {
        facet_result: savedFacetResult,
        ...(ver !== undefined ? { version: ver } : {}),
    });
    return savedFacetResult;
}

export function syncFacetMoreToStore(
    updateFacet: (id: string, updates: Record<string, unknown>) => void,
    facetId: string,
    resp: unknown,
    nextMore: Record<string, unknown>
): void {
    const ver = facetServerVersionFromUpdateResponse(resp);
    updateFacet(facetId, {
        more: nextMore,
        ...(ver !== undefined ? { version: ver } : {}),
    });
}

interface MutateFacetUpdateArgs {
    facet: FacetSnapshotForUpdate;
    notebookId?: number;
    payloadOverrides: {
        etl_json: Record<string, unknown>;
        facet_result: Record<string, unknown>;
        more?: Record<string, unknown>;
    };
    missingServerMessage: string;
    mutate: UseMutateFunction<unknown, unknown, UpdateFacetPayload, unknown>;
    onSuccess: (resp: unknown) => void;
    onError?: (error: unknown) => void;
}

export function mutateFacetUpdate({
    facet,
    notebookId,
    payloadOverrides,
    missingServerMessage,
    mutate,
    onSuccess,
    onError,
}: MutateFacetUpdateArgs): boolean {
    const payload = buildFacetUpdatePayload(facet, payloadOverrides, notebookId);
    if (!payload) {
        toast.error(missingServerMessage);
        return false;
    }
    mutate(payload, { onSuccess, onError });
    return true;
}
